import { Snowflake, Package, Droplet, Beef } from "lucide-react";
import { Card } from "@/components/ExcelUI";
import { hamKaasBatchRest } from "@/lib/hamKaasVoorraad";
import { sojaolieLedger } from "@/lib/sojaolieVoorraad";

// Read-only koelcel-overzicht: grondstoffen + ham/kaas + olie in één scherm.
// Herbruikt exact dezelfde voorraadberekeningen als de eigen tabbladen (grondBatchStats,
// hamKaasBatchRest, sojaolieLedger). Raakt GEEN afboeklogica, FIFO, recall of beveiliging aan.
export default function KoelcelOverzicht({ xl }) {
  const { euro, kgf, toNum, showCosts, entries, lotConfig, rows, helpers, hulpLedger } = xl;
  const lf = (v) => Number(v || 0).toLocaleString("nl-NL", { maximumFractionDigits: 1 });

  // Rauwe (ingekochte) ham & kaas uit dezelfde beschermde hulpstof-ledger — geen eigen
  // voorraadlogica. Snijden boekt deze 50/50 af (halffabRuns → hulpLedger) en maakt een
  // invriespartij; daarom staat vers hier en gesneden/vriezer apart (geen dubbeltelling).
  const hkNamen = new Set((helpers || []).filter((h) => h.type === "Ham/kaas").map((h) => h.naam));
  const versAgg = {};
  ((hulpLedger && hulpLedger.lots) || []).forEach((l) => {
    const isHk = l.type === "Ham/kaas" || (Array.isArray(l.names) && l.names.some((nm) => hkNamen.has(nm)));
    if (!isHk) return;
    const rest = Math.max(toNum(l.rest), 0);
    if (rest <= 0.001) return;
    const k = l.naam || "Ham/kaas";
    if (!versAgg[k]) versAgg[k] = { naam: k, rest: 0, waarde: 0 };
    versAgg[k].rest += rest;
    versAgg[k].waarde += rest * (toNum(l.prijs) || 0);
  });
  const versRows = Object.values(versAgg).sort((a, b) => b.waarde - a.waarde);

  const grondAgg = {};
  (xl.grondBatchStats() || []).forEach((r) => {
    const rest = Math.max(r.rest, 0);
    if (rest <= 0.001) return;
    const k = r.naam;
    if (!grondAgg[k]) grondAgg[k] = { naam: k, rest: 0, waarde: 0 };
    grondAgg[k].rest += rest;
    grondAgg[k].waarde += rest * (r.prijs || 0);
  });
  const grondRows = Object.values(grondAgg).sort((a, b) => b.waarde - a.waarde);

  const hkAgg = {};
  (xl.hamKaasBatches || []).forEach((b) => {
    const rest = Math.max(hamKaasBatchRest(b, entries), 0);
    if (rest <= 0.001) return;
    const k = b.naam || "Ham/kaas";
    if (!hkAgg[k]) hkAgg[k] = { naam: k, rest: 0, waarde: 0 };
    hkAgg[k].rest += rest;
    hkAgg[k].waarde += rest * (toNum(b.prijs) || 0);
  });
  const hkRows = Object.values(hkAgg).sort((a, b) => b.waarde - a.waarde);

  const cfg = (lotConfig && lotConfig.sojaolieIbc) || {};
  const ontvangen = Array.isArray(cfg.ontvangen) ? cfg.ontvangen : [];
  const correcties = Array.isArray(cfg.correcties) ? cfg.correcties : [];
  const consumption = (rows || []).filter((r) => r.gaatDoorFrituurolie && toNum(r.sojaolieKosten) > 0).map((r) => ({ datum: r.datum, euro: toNum(r.sojaolieKosten) }));
  const olie = sojaolieLedger(ontvangen, consumption, correcties);
  const heeftOlie = (olie.totaalRest || 0) > 0.001 || (olie.totaalWaarde || 0) > 0.001;

  const totGrond = grondRows.reduce((s, r) => s + r.waarde, 0);
  const totVers = versRows.reduce((s, r) => s + r.waarde, 0);
  const totHk = hkRows.reduce((s, r) => s + r.waarde, 0);
  const totOlie = olie.totaalWaarde || 0;
  const totaal = totGrond + totVers + totHk + totOlie;

  const KpiCard = ({ icon: Icon, label, value, sub, accent }) => (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${accent}`}>
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500"><Icon className="h-3.5 w-3.5" />{label}</div>
      <div className="mt-2 text-xl font-bold tabular-nums text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );

  const Badge = ({ color, children }) => <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${color}`}>{children}</span>;

  const section = [
    { key: "grond", label: "Grondstof", color: "bg-amber-100 text-amber-700", icon: Package, unit: "kg", rows: grondRows.map((r) => ({ naam: r.naam, voorraad: kgf(r.rest), waarde: r.waarde, prijs: r.rest > 0 ? r.waarde / r.rest : 0 })) },
    { key: "hkvers", label: "Ham/kaas vers", color: "bg-rose-100 text-rose-700", icon: Beef, unit: "kg", rows: versRows.map((r) => ({ naam: r.naam, voorraad: kgf(r.rest), waarde: r.waarde, prijs: r.rest > 0 ? r.waarde / r.rest : 0 })) },
    { key: "hkvries", label: "Ham/kaas vriezer", color: "bg-cyan-100 text-cyan-700", icon: Snowflake, unit: "kg", rows: hkRows.map((r) => ({ naam: `${r.naam} · gesneden`, voorraad: kgf(r.rest), waarde: r.waarde, prijs: r.rest > 0 ? r.waarde / r.rest : 0 })) },
    { key: "olie", label: "Olie", color: "bg-blue-100 text-blue-700", icon: Droplet, unit: "L", rows: heeftOlie ? [{ naam: "Sojaolie (IBC, FIFO)", voorraad: `${lf(olie.totaalRest)} L`, waarde: totOlie, prijs: olie.totaalRest > 0 ? totOlie / olie.totaalRest : 0 }] : [] },
  ];
  const alleRijen = section.flatMap((s) => s.rows.map((r) => ({ ...r, cat: s })));

  return (
    <Card className="mb-6" data-testid="koelcel-overzicht">
      <div className="flex items-start gap-3 mb-1">
        <div className="rounded-lg bg-sky-100 p-2 text-sky-700"><Snowflake className="h-5 w-5" /></div>
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Koelcel-overzicht</h3>
          <p className="text-sm text-slate-500">Grondstoffen, <b>verse ham &amp; kaas</b>, <b>gesneden ham/kaas (vriezer)</b> én olie samen in één voorraadoverzicht. Dit is een <b>read-only</b> weergave — inboeken, snijden, correcties en FIFO-afboeking doe je in de eigen tabbladen; die blijven leidend. Snijden boekt verse ham &amp; kaas automatisch 50/50 af.</p>
        </div>
      </div>
      {showCosts && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 my-4">
          <KpiCard icon={Package} label="Grondstoffen" value={euro(totGrond)} accent="border-amber-200" />
          <KpiCard icon={Beef} label="Ham / kaas" value={euro(totVers + totHk)} sub={`vers ${euro(totVers)} · vriezer ${euro(totHk)}`} accent="border-rose-200" />
          <KpiCard icon={Droplet} label="Olie" value={euro(totOlie)} sub={heeftOlie ? `${lf(olie.totaalRest)} L` : "geen voorraad"} accent="border-blue-200" />
          <KpiCard icon={Snowflake} label="Totaal koelcel" value={euro(totaal)} accent="border-sky-300 bg-sky-50/40" />
        </div>
      )}
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm" data-testid="koelcel-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Categorie</th>
              <th className="text-left px-3 py-2 font-semibold">Item</th>
              <th className="text-right px-3 py-2 font-semibold">Voorraad</th>
              {showCosts && <th className="text-right px-3 py-2 font-semibold">Prijs/eenheid</th>}
              {showCosts && <th className="text-right px-3 py-2 font-semibold">Voorraadwaarde</th>}
            </tr>
          </thead>
          <tbody>
            {alleRijen.length === 0 ? (
              <tr><td colSpan={showCosts ? 5 : 3} className="text-center text-slate-400 py-8">Nog geen voorraad in de koelcel.</td></tr>
            ) : alleRijen.map((r, i) => (
              <tr key={`${r.cat.key}-${r.naam}-${i}`} data-testid="koelcel-row" className="border-t border-slate-100 hover:bg-slate-50/80">
                <td className="px-3 py-2"><Badge color={r.cat.color}><r.cat.icon className="h-3 w-3" />{r.cat.label}</Badge></td>
                <td className="px-3 py-2 font-medium text-slate-800">{r.naam}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-700">{r.voorraad}</td>
                {showCosts && <td className="px-3 py-2 text-right tabular-nums text-slate-500">{euro(r.prijs)}</td>}
                {showCosts && <td className="px-3 py-2 text-right tabular-nums font-semibold">{euro(r.waarde)}</td>}
              </tr>
            ))}
          </tbody>
          {showCosts && alleRijen.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-200 bg-slate-50 font-bold text-slate-800">
                <td className="px-3 py-2" colSpan={4}>Totale voorraadwaarde koelcel</td>
                <td className="px-3 py-2 text-right tabular-nums text-[#1C64B0]" data-testid="koelcel-totaal">{euro(totaal)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </Card>
  );
}
